import { z } from 'zod';

/**
 * Card location contracts — B54, `FR-VIEW-028` / `FR-VIEW-029`.
 *
 * A location is address text plus a complete coordinate pair. Partial
 * coordinates are rejected here and in Postgres, and coordinates are normalised
 * to the storage scale so exact equality is enough to cluster map pins.
 */

export const CARD_LOCATION_TEXT_MAX_LENGTH = 512;
export const CARD_LOCATION_COORDINATE_SCALE = 7;

function normaliseCoordinate(value: number): number {
  const normalised = Number(value.toFixed(CARD_LOCATION_COORDINATE_SCALE));
  return Object.is(normalised, -0) ? 0 : normalised;
}

export const cardLocationLatitudeSchema = z
  .number()
  .finite()
  .min(-90)
  .max(90)
  .transform(normaliseCoordinate);

export type CardLocationLatitude = z.infer<typeof cardLocationLatitudeSchema>;

export const cardLocationLongitudeSchema = z
  .number()
  .finite()
  .min(-180)
  .max(180)
  .transform(normaliseCoordinate);

export type CardLocationLongitude = z.infer<typeof cardLocationLongitudeSchema>;

export const cardLocationTextSchema = z
  .string()
  .trim()
  .max(CARD_LOCATION_TEXT_MAX_LENGTH)
  .transform((value) => (value.length === 0 ? null : value));

export const cardLocationSchema = z.strictObject({
  text: z.string().max(CARD_LOCATION_TEXT_MAX_LENGTH).nullable(),
  lat: z.number().finite().min(-90).max(90),
  lng: z.number().finite().min(-180).max(180),
});

export type CardLocation = z.infer<typeof cardLocationSchema>;

export const cardLocationPutSchema = z.strictObject({
  text: cardLocationTextSchema.nullable().default(null),
  lat: cardLocationLatitudeSchema,
  lng: cardLocationLongitudeSchema,
});

export type CardLocationPut = z.infer<typeof cardLocationPutSchema>;

export function cardLocationCoordinateStorageValue(value: number): string {
  const normalised = normaliseCoordinate(value);
  return normalised.toFixed(CARD_LOCATION_COORDINATE_SCALE);
}
